import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { ActiveFilters } from '@/types/filter/ActiveFilters.types';

import { ShowingApiGroup } from '@cineamo/api/resources/showing/showing-api.group';

import { CachedContentData, getCachedContentData } from '@/hooks/useCachedContentData';
import { applyClientFilters, getFutureShowingsFiltered } from '@/hooks/useFutureShowings';

import {
    dateTodayPlus,
    formatDate_yyyyMMddTHHmmssZ,
    getDateAtHour,
    getDates,
    getLogicalEndOfDay,
    getLogicalStartOfDay,
    logicalStartOfToday
} from '@/utils/date-helper';

type GetShowingsOptions = {
    cinemaId: number;
    startDate: Date | string;
    filter?: ActiveFilters;
};

export const getStartDatetime = (options: GetShowingsOptions) => {
    const startDate =
        options.startDate === 'all'
            ? logicalStartOfToday()
            : options.startDate === 'soon'
              ? dateTodayPlus(14)
              : options.startDate;
    const startDatetime =
        'startTime' in (options.filter ?? {})
            ? getDateAtHour(startDate, Number(options.filter.startTime))
            : getLogicalStartOfDay(startDate);
    return formatDate_yyyyMMddTHHmmssZ(startDatetime);
};

export const getEndDatetime = (options: GetShowingsOptions) => {
    const endDatetime =
        'endTime' in (options.filter ?? {})
            ? getDateAtHour(options.startDate, Number(options.filter.endTime))
            : getLogicalEndOfDay(options.startDate);

    return ['all', 'soon'].includes(options.startDate.toString())
        ? undefined
        : formatDate_yyyyMMddTHHmmssZ(endDatetime);
};

export async function getContentsWithShowings(
    options: GetShowingsOptions,
    cinema: CinemaDto,
    locale: string
): Promise<CachedContentData[]> {
    const startDatetime = getStartDatetime(options);
    const endDatetime = getEndDatetime(options);
    const showings = await getFutureShowingsFiltered(
        options.cinemaId,
        {
            startDatetime,
            endDatetime,
            group: ShowingApiGroup.Content,
            ...options.filter
        },
        null,
        { locale }
    );

    return await Promise.all(
        showings.map((showing) =>
            getCachedContentData({
                content: showing._embedded.content,
                cinema: cinema,
                locale
            })
        )
    );
}

const filterShowingsForDay = (showings: ShowingDto[], date: Date): ShowingDto[] => {
    const dayStart = getLogicalStartOfDay(date);
    const dayEnd = getLogicalEndOfDay(date);

    return showings.filter((showing) => {
        const showingDate = new Date(showing.startDatetime);
        return showingDate >= dayStart && showingDate <= dayEnd;
    });
};

const filterShowingsAfterDate = (showings: ShowingDto[], date: Date): ShowingDto[] => {
    const startDate = getLogicalStartOfDay(date);

    return showings.filter((showing) => {
        const showingDate = new Date(showing.startDatetime);
        return showingDate >= startDate;
    });
};

export async function getShowingCountsForDays(options: {
    contentId?: number;
    cinemaId: number;
    daysCount: number;
    filter?: ActiveFilters;
    /**
     * Raw, unfiltered `showings-future` response of the cinema; when provided, skips the HTTP
     * fetch. Not compatible with tag filters (`showingTagIds`/`hasShowingTags`), which are only
     * applied server-side.
     */
    showings?: ShowingDto[];
}): Promise<number[]> {
    const showingsQuery = {
        contentId: options.contentId,
        ...options.filter
    };
    const allShowings = options.showings
        ? await applyClientFilters(options.showings, showingsQuery)
        : await getFutureShowingsFiltered(options.cinemaId, showingsQuery);

    // Group showings by day for the specified period
    const dates = getDates(options.daysCount + 1);
    const showingsByDate = dates.slice(0, options.daysCount).map((date) => filterShowingsForDay(allShowings, date));

    // Add "soon" showings as the final element
    const soonStartDate = dates[options.daysCount];
    const soonShowings = filterShowingsAfterDate(allShowings, soonStartDate);
    showingsByDate.push(soonShowings);

    return showingsByDate.map((showings) => showings.length);
}
